import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, DatePicker, Input, Progress, Row, Select, Table, Tag, Typography } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import { fetchMifirActiveProcess, fetchMifirProcessHistory, sendNotification } from "../redux/actions";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Browser status reads are DB-only, so keep the UI responsive without consuming QuotaGuard.
const MIFIR_STATUS_POLL_INTERVAL_MS = 2000;


const displayStatus = status => status === "SUCCEEDED" ? "SUCCESSFUL" : status;

const statusColor = status => {
  if (status === "SUCCEEDED") return "green";
  if (["FAILED", "UNKNOWN"].includes(status)) return "red";
  if (status === "ABORTED") return "orange";
  if (["STARTING", "QUEUED", "RUNNING"].includes(status)) return "blue";
  return "default";
};

const formatUtcDateTimeText = value => {
  if (!value) return "";
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss [UTC]") : String(value);
};

const formatDateTime = value => {
  if (!value) return null;
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return <span>{String(value)}</span>;
  return (
    <span className="mifir-date-time-cell">
      <span>{parsed.format("YYYY-MM-DD")}</span>
      <span>{parsed.format("HH:mm:ss [UTC]")}</span>
    </span>
  );
};


// Keep Process Status customer-facing even for historical rows that may contain
// the older technical send_report_errors.sgrf wording.
const displayProcessMessage = (value, record = {}) => {
  if (!value) return "";
  const raw = String(value).trim();
  const upper = raw.toUpperCase();
  const isFeedback = record.process_type === "FEEDBACK";

  if (upper.includes("UNKNOWN_NCA_USERNAME") || upper.includes("UNKNOWN NCA USERNAME") || upper.includes("NCA USERNAME REJECTED")) {
    return "The NCA username was not accepted. Please check the username and try again.";
  }
  if (upper.includes("UNKNOWN_NCA_PASSWORD_HASH") || upper.includes("UNKNOWN NCA PASSWORD HASH") || upper.includes("NCA PASSWORD REJECTED")) {
    return "The NCA password was not accepted. Please check the password and try again.";
  }
  if (upper.includes("UNKNOWN_NCA_HOSTNAME") || upper.includes("UNKNOWN NCA HOSTNAME") || upper.includes("NCA CONNECTION CONFIGURATION ERROR")) {
    return "The NCA connection is not configured correctly for this account. Please contact Finmatek Support.";
  }
  if (upper.includes("UNKNOWN_CPARTY_CODE") || upper.includes("UNKNOWN CPARTY CODE") || upper.includes("UNKNOWN COUNTERPARTY CODE") || upper.includes("CUSTOMER REPORTING CONFIGURATION ERROR")) {
    return "The customer reporting configuration is incomplete. Please contact Finmatek Support.";
  }
  if (upper.includes("SFTP_CONNECTION_ERROR") || upper.includes("SFTP CONNECTION ERROR") || upper.includes("NCA CONNECTION ERROR")) {
    return "A connection to the NCA reporting service could not be established. Please try again later. If the issue persists, contact Finmatek Support.";
  }
  if (upper.includes("DATABASE_CONNECTION_ERROR") || upper.includes("DATABASE CONNECTION ERROR") || upper.includes("REPORTING DATA ACCESS ERROR")) {
    return "The reporting service could not access the required data. Please try again. If the issue persists, contact Finmatek Support.";
  }
  if (upper.includes("XML_VALIDATION_ERROR") || upper.includes("XML VALIDATION ERROR")) {
    return isFeedback
      ? "The NCA feedback request could not be completed because the exchanged XML data did not pass validation. Please contact Finmatek Support."
      : "The report could not be submitted because the XML file did not pass validation. Please review the report errors and reprepare the report before trying again.";
  }
  if (upper.includes("GENERATION_ERROR_DETECTED") || upper.includes("REPORT GENERATION ERROR") || upper.includes("REPORT ERROR DETECTED")) {
    return isFeedback
      ? "Feedback retrieval could not be completed because a report error was detected. Please review the report and try again after the issue is corrected."
      : "The report contains errors that prevent submission. Please review the report errors, correct them and reprepare the report before trying again.";
  }
  if (upper.includes("NCA ERROR-HANDLING PATH") || upper.includes("UNKNOWN NCA PROCESSING ERROR") || upper.includes("UNEXPECTED PROCESSING ERROR")) {
    return isFeedback
      ? "NCA feedback retrieval could not be completed. Please check the NCA credentials and try again. If the issue persists, contact Finmatek Support."
      : "NCA submission could not be completed. Please check the NCA credentials and try again. If the issue persists, contact Finmatek Support.";
  }

  return raw
    .replace(/generation errors/gi, "report errors")
    .replace(/generated errors/gi, "report errors");
};

const columnSearchProps = (dataIndex, valueGetter) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div className="mifir-column-filter">
      <Input
        autoFocus
        placeholder="Filter..."
        value={selectedKeys[0] || ""}
        onChange={event => setSelectedKeys(event.target.value ? [event.target.value] : [])}
        onPressEnter={() => confirm()}
      />
      <div className="mifir-column-filter__actions">
        <Button type="primary" size="small" onClick={() => confirm()}>Apply</Button>
        <Button
          size="small"
          onClick={() => {
            if (clearFilters) clearFilters();
            confirm();
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  onFilter: (value, record) => {
    const raw = valueGetter ? valueGetter(record) : record[dataIndex];
    return String(raw === null || typeof raw === "undefined" ? "" : raw)
      .toLowerCase()
      .includes(String(value).toLowerCase());
  },
});

const ProgressDisplay = ({ record, compact = false }) => {
  const hasPercent = record.progress_percent !== null
    && typeof record.progress_percent !== "undefined"
    && record.progress_percent !== "";
  const percent = hasPercent && Number.isFinite(Number(record.progress_percent))
    ? Number(record.progress_percent)
    : null;
  const active = ["STARTING", "QUEUED", "RUNNING"].includes(record.status);

  if (record.status === "SUCCEEDED") {
    return <Progress percent={100} size="small" status="success" showInfo={!compact} />;
  }

  if (percent !== null) {
    return (
      <Progress
        percent={Math.max(0, Math.min(100, percent))}
        size="small"
        status={record.status === "FAILED" ? "exception" : (active ? "active" : "normal")}
        showInfo={!compact}
      />
    );
  }

  if (active) {
    return (
      <div>
        <div className="mifir-progress-indeterminate" title="Progress is being tracked">
          <span />
        </div>
        {!compact && <Text type="secondary" style={{ fontSize: 11 }}>In progress</Text>}
      </div>
    );
  }

  return <Text type="secondary">—</Text>;
};

const uniqueValues = values => Array.from(new Set(values.filter(Boolean))).sort();

const MiFIRProcessStatusPage = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [activeProcess, setActiveProcess] = useState(null);
  const [activeStatusLoaded, setActiveStatusLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [processType, setProcessType] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [platform, setPlatform] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [knownPlatforms, setKnownPlatforms] = useState([]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const historyOptions = {
        page,
        pageSize,
        startDate: dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null,
        endDate: dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null,
        processType,
        tradingPlatform: platform,
        status,
      };

      const [historyResult, activeResult] = await Promise.all([
        dispatch(fetchMifirProcessHistory(historyOptions)),
        dispatch(fetchMifirActiveProcess()).catch(() => ({ payload: null, _activeRequestFailed: true })),
      ]);
      const historyPayload = historyResult.payload || {};
      const historyRows = historyPayload.rows || [];
      const liveActive = activeResult.payload || null;

      setTotal(Number.isFinite(Number(historyPayload.total)) ? Number(historyPayload.total) : historyRows.length);
      setKnownPlatforms(previous => uniqueValues([
        ...previous,
        ...historyRows.map(row => row.trading_platform),
        platform !== "ALL" ? platform : null,
      ]));

      if (!activeResult._activeRequestFailed) {
        setActiveProcess(liveActive);
        setActiveStatusLoaded(true);
      }

      // The active endpoint is authoritative for the live row. If the same row
      // is already on the current DB page, merge the fresher active snapshot.
      // Do not inject it into a different filtered/page result because that
      // would make the server-side pagination count inconsistent.
      if (liveActive) {
        const index = historyRows.findIndex(row => Number(row.id) === Number(liveActive.id));
        if (index >= 0) {
          historyRows[index] = { ...historyRows[index], ...liveActive };
        }
      }
      setRows(historyRows);
    } catch (err) {
      if (!silent) {
        dispatch(sendNotification({
          type: "error",
          message: "MiFIR Process Status",
          description: "Could not load MiFIR process status.",
        }));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dispatch, page, pageSize, dateRange, processType, platform, status]);

  useEffect(() => {
    let disposed = false;
    let inFlight = false;

    const poll = async (silent = true) => {
      if (disposed || inFlight) return;
      inFlight = true;
      try {
        await load(silent);
      } finally {
        inFlight = false;
      }
    };

    let timer = null;

    const scheduleNextPoll = () => {
      if (disposed) return;
      timer = setTimeout(async () => {
        await poll(true);
        scheduleNextPoll();
      }, MIFIR_STATUS_POLL_INTERVAL_MS);
    };

    Promise.resolve(poll(false)).finally(scheduleNextPoll);
    const onFocus = () => poll(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll(true);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disposed = true;
      clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const processTypeOptions = ["PREPARATION", "REPREPARATION", "SUBMISSION", "FEEDBACK"];
  const statusOptions = ["STARTING", "QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "ABORTED", "UNKNOWN"];
  const platformOptions = knownPlatforms;

  // Top-level filters and pagination are applied by PostgreSQL. Table column
  // search filters remain local to the currently displayed page.
  const filteredRows = rows;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [page, pageSize, total]);

  const columns = useMemo(() => [
    {
      title: "Process Type",
      dataIndex: "process_type",
      key: "process_type",
      width: "8%",
      align: "center",
      ...columnSearchProps("process_type", record => record.is_repreparation ? "REPREPARATION" : record.process_type),
      render: (value, record) => (
        <Tag className="mifir-process-type-tag" color={record.is_repreparation ? "orange" : undefined}>
          {record.is_repreparation ? "REPREPARATION" : value}
        </Tag>
      ),
    },
    { title: "Report Date", dataIndex: "report_date", key: "report_date", width: "7%", align: "center", ...columnSearchProps("report_date") },
    { title: "Trading Platform", dataIndex: "trading_platform", key: "trading_platform", width: "9%", align: "center", ...columnSearchProps("trading_platform") },
    {
      title: "Progress",
      key: "progress",
      width: "7%",
      align: "center",
      ...columnSearchProps("progress", record => {
        if (record.progress_percent !== null && typeof record.progress_percent !== "undefined") return record.progress_percent;
        return displayStatus(record.status);
      }),
      render: (_, record) => <ProgressDisplay record={record} compact />,
    },
    {
      title: "Report Error",
      dataIndex: "generation_error",
      key: "generation_error",
      width: "7%",
      align: "center",
      ...columnSearchProps("generation_error", record => record.generation_error ? "Yes" : "No"),
      render: (value, record) => record.process_type === "FEEDBACK"
        ? <Text type="secondary">—</Text>
        : (value
          ? <Tag color="orange">Yes</Tag>
          : <Tag color="green">No</Tag>),
    },
    {
      title: "Repreparation Reason",
      dataIndex: "reprepare_reason",
      key: "reprepare_reason",
      width: "10%",
      ...columnSearchProps("reprepare_reason"),
      render: value => value ? <div className="mifir-readable-wrap" title={value}>{value}</div> : "",
    },
    {
      title: "Process Status",
      dataIndex: "status",
      key: "status",
      width: "6%",
      align: "center",
      ...columnSearchProps("status", record => displayStatus(record.status)),
      render: value => <Tag color={statusColor(value)}>{displayStatus(value)}</Tag>,
    },
    { title: "Run ID", dataIndex: "run_id", key: "run_id", width: "5%", align: "center", ...columnSearchProps("run_id") },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: "17%",
      ...columnSearchProps("message", record => displayProcessMessage(record.message, record)),
      render: (value, record) => {
        const message = displayProcessMessage(value, record);
        return message ? <div className="mifir-readable-wrap" title={message}>{message}</div> : "";
      },
    },
    {
      title: "Started (UTC)",
      dataIndex: "started_at",
      key: "started_at",
      width: "7%",
      align: "center",
      ...columnSearchProps("started_at", record => formatUtcDateTimeText(record.started_at)),
      render: formatDateTime,
    },
    {
      title: "Finished (UTC)",
      dataIndex: "finished_at",
      key: "finished_at",
      width: "7%",
      align: "center",
      ...columnSearchProps("finished_at", record => formatUtcDateTimeText(record.finished_at)),
      render: formatDateTime,
    },
  ], []);

  const active = activeStatusLoaded
    ? activeProcess
    : (activeProcess || rows.find(row => ["STARTING", "QUEUED", "RUNNING"].includes(row.status)));

  return (
    <div className="mifir-process-page">
      <div className="mifir-page-heading mifir-page-heading--simple">
        <div>
          <h2>MiFIR Process Status</h2>
          <Text type="secondary">Review process history and filter by report date, process type, platform and process status.</Text>
        </div>
      </div>

      <div className="mifir-toolbar">
        <Row className="mifir-toolbar-row" gutter={[10, 6]} align="middle">
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Report Date Range</Text>
              <RangePicker
                className="mifir-filter-range"
                value={dateRange}
                onChange={value => {
                  setDateRange(value);
                  setPage(1);
                }}
                format="YYYY-MM-DD"
                allowClear
              />
            </div>
          </Col>
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Process Type</Text>
              <Select
                className="mifir-filter-select"
                value={processType}
                onChange={value => { setProcessType(value); setPage(1); }}
              >
                <Option value="ALL">All</Option>
                {processTypeOptions.map(value => <Option key={value} value={value}>{value}</Option>)}
              </Select>
            </div>
          </Col>
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Trading Platform</Text>
              <Select
                className="mifir-filter-select"
                value={platform}
                onChange={value => { setPlatform(value); setPage(1); }}
              >
                <Option value="ALL">All</Option>
                {platformOptions.map(value => <Option key={value} value={value}>{value}</Option>)}
              </Select>
            </div>
          </Col>
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Process Status</Text>
              <Select
                className="mifir-filter-select"
                value={status}
                onChange={value => { setStatus(value); setPage(1); }}
              >
                <Option value="ALL">All</Option>
                {statusOptions.map(value => <Option key={value} value={value}>{displayStatus(value)}</Option>)}
              </Select>
            </div>
          </Col>
          <Col>
            <div className="mifir-toolbar-actions">
              <Button type="primary" onClick={() => load()} loading={loading}>Refresh</Button>
            </div>
          </Col>
        </Row>
      </div>

      {active && (
        <div className="mifir-active-process-card mifir-active-process-card--progress">
          <div className="mifir-active-process-card__summary">
            <Text strong>
              Active process: {active.is_repreparation ? "REPREPARATION" : active.process_type}
              {active.trading_platform ? ` - ${active.trading_platform}` : ""}
            </Text>
            <Tag color={statusColor(active.status)}>{active.status}</Tag>
          </div>
          <ProgressDisplay record={active} />
          {active.message ? (
            <Text className="mifir-active-process-card__message">
              {displayProcessMessage(active.message, active)}
            </Text>
          ) : null}
        </div>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: totalRows => `${totalRows} processes`,
          size: "small",
        }}
        onChange={pagination => {
          const nextPageSize = Number(pagination.pageSize) || pageSize;
          if (nextPageSize !== pageSize) {
            setPageSize(nextPageSize);
            setPage(1);
          } else {
            setPage(Number(pagination.current) || 1);
          }
        }}
        size="small"
        tableLayout="fixed"
        className="mifir-status-table mifir-status-table--fit"
        rowClassName={(record, index) => index % 2 === 0 ? "mifir-table-row-even" : "mifir-table-row-odd"}
      />
    </div>
  );
};

export default MiFIRProcessStatusPage;
